import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { WORKER_LINE_STATUS_OPTIONS, statusBadgeVariant, formatDateTimeTh, describeDetailError, describeWriteError, nextActionLabel, fieldLabel } from "./workerLineShared.js";
import AgentDelegatedActionsPanel from "./AgentDelegatedActionsPanel.jsx";
import { getAgentPhotoBlob } from "./workerLineApi.js";

import { DetailSkeleton } from "../components/Skeleton.jsx";
/**
 * Shared Agent detail/create/edit drawer views (Prompt 132), reused by both
 * ./AgentsListPage.jsx and ./WorkerLineOrgChartPage.jsx (an Agent node's own
 * drawer) over the real GET/POST/PATCH .../worker-line/agents API. Agent has
 * no further "create under me" next action (it is the deepest Worker Line
 * tier). P145 added AgentDelegatedActionsPanel below, so a SuperAdmin /
 * Super Agent / PLC viewing an Agent here can issue that Agent's Invite
 * Cards and reach their workers' Buddy Guarantee - the "a larger role may
 * act on behalf of a smaller one" rule. The nextActions codes stay
 * informational labels; the panel is the real, working affordance.
 */

/**
 * P161: what the agent looks like, or a labelled placeholder when no picture
 * has been attached.
 *
 * The bytes are fetched with the session token and turned into an object URL -
 * the endpoint is authenticated, so pointing an `<img src>` straight at it
 * would 401 and render a broken image. The URL is revoked on unmount and
 * whenever the agent changes, so a drawer opened and closed all afternoon does
 * not leak one blob per open.
 *
 * `hasPhoto` comes from the record the caller already has, so a agent with no
 * picture costs no request at all rather than a guaranteed 404 per render.
 */
/**
 * P165: exported so the Super Agent drill-down's own Agent page can show the
 * same picture rather than growing a second copy of this fetch-as-blob dance.
 * The bytes come from an authenticated route, so a bare <img src> would 401.
 */
export function AgentPhoto({ agentId, hasPhoto, displayName, size = "lg" }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!agentId || !hasPhoto) {
      setObjectUrl(null);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    let created = null;
    setFailed(false);

    getAgentPhotoBlob(agentId)
      .then(({ blob }) => {
        if (cancelled) {
          return;
        }
        // jsdom has no createObjectURL; treat its absence as "cannot preview"
        // rather than crashing the whole drawer around it.
        if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
          setFailed(true);
          return;
        }
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });

    return () => {
      cancelled = true;
      if (created && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
        URL.revokeObjectURL(created);
      }
    };
  }, [agentId, hasPhoto]);

  const className = `wx-wl-agent-photo wx-wl-agent-photo--${size}`;

  if (objectUrl) {
    return <img className={className} src={objectUrl} alt={`รูป ${displayName || "Agent"}`} />;
  }

  return (
    <div className={`${className} wx-wl-agent-photo--empty`} role="img" aria-label={hasPhoto && failed ? "ไม่สามารถแสดงรูปได้" : "ยังไม่มีรูป"}>
      <span aria-hidden="true">{hasPhoto && failed ? "!" : (displayName || "?").trim().charAt(0).toUpperCase()}</span>
    </div>
  );
}

export function AgentDetailView({ titleId, state, onEdit, onManageLoginAccount, canIssueInviteLink = false }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-wl-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const nextActions = detail.nextActions ?? [];
  const parentSuperAgent = detail.parentSuperAgent ?? {};
  const parentPlc = detail.parentPlc ?? {};

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">AGENT · DETAIL</span>
      {/* P161: the picture sits beside the name rather than in a section of its
          own - it identifies the person this record is about, which is what the
          top of the drawer is for. */}
      <div className="wx-wl-drawer-identity">
        <AgentPhoto agentId={detail.agentId} hasPhoto={Boolean(detail.photo)} displayName={detail.displayName} />
        <div>
          <h2 id={titleId} className="wx-wl-drawer-title">
            {detail.displayName}
          </h2>
          <p className="wx-wl-drawer-sub">{detail.agentCode}</p>
          <div className="wx-wl-drawer-status">
            <span className={`wx-badge wx-badge--${statusBadgeVariant(detail.status)}`}>{detail.status}</span>
          </div>
        </div>
      </div>

      <section className="wx-wl-dsec">
        <h3>สายงานต้นสังกัด</h3>
        <div className="wx-wl-kv-row">
          <span>Super Agent</span>
          <strong>{parentSuperAgent.displayName || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>PLC / บมจ</span>
          <strong>{parentPlc.displayName || "-"}</strong>
        </div>
        {parentSuperAgent.superAgentOrganizationId && (
          <p className="wx-wl-note-muted">
            <Link to={`/backoffice/super-agents/${parentSuperAgent.superAgentOrganizationId}`}>เปิด Super Agent นี้</Link>
          </p>
        )}
      </section>

      <section className="wx-wl-dsec">
        <h3>ข้อมูลทั่วไป / ผู้ติดต่อ</h3>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("legalName")}</span>
          <strong>{detail.legalName || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("ownerName")}</span>
          <strong>{detail.ownerName || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("primaryPhoneNumber")}</span>
          <strong>{detail.primaryPhoneNumber || "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("lineId")}</span>
          <strong>{detail.lineId || "-"}</strong>
        </div>
        {detail.note && (
          <div className="wx-wl-kv-row">
            <span>{fieldLabel("note")}</span>
            <strong>{detail.note}</strong>
          </div>
        )}
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("createdAtUtc")}</span>
          <strong>{formatDateTimeTh(detail.createdAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-wl-kv-row">
          <span>{fieldLabel("updatedAtUtc")}</span>
          <strong>{formatDateTimeTh(detail.updatedAtUtc) ?? "-"}</strong>
        </div>
      </section>

      <section className="wx-wl-dsec">
        <h3>บัญชีเข้าสู่ระบบ Backoffice (Login Account)</h3>
        <div className="wx-wl-kv-row">
          <span>มีบัญชีเข้าสู่ระบบ</span>
          <strong>{detail.loginAccount?.hasLoginAccount ? "มี" : "ยังไม่มี"}</strong>
        </div>
        {detail.loginAccount?.hasLoginAccount && (
          <>
            <div className="wx-wl-kv-row">
              <span>ชื่อผู้ใช้สำหรับเข้าระบบ</span>
              <strong>{detail.loginAccount.loginIdentifier || "-"}</strong>
            </div>
            <div className="wx-wl-kv-row">
              <span>role / scope</span>
              <strong>
                {detail.loginAccount.roleCode || "-"} / {detail.loginAccount.scopeType || "-"} (Agent นี้เท่านั้น)
              </strong>
            </div>
          </>
        )}
        {onManageLoginAccount && (
          <button type="button" className="wx-button wx-button--ghost wx-wl-drawer-actions-full" onClick={onManageLoginAccount}>
            {detail.loginAccount?.hasLoginAccount ? "รีเซ็ตรหัสผ่าน Agent" : "สร้างบัญชีเข้าสู่ระบบ Agent"}
          </button>
        )}
      </section>

      <section className="wx-wl-dsec">
        <h3>จำนวนรวม</h3>
        <div className="wx-wl-counts-grid">
          <div className="wx-wl-count-cell">
            <span>คำเชิญ</span>
            <strong>{detail.inviteCount}</strong>
          </div>
          <div className="wx-wl-count-cell">
            <span>ใบสมัคร</span>
            <strong>{detail.applicationCount}</strong>
          </div>
          <div className="wx-wl-count-cell">
            <span>แรงงาน</span>
            <strong>{detail.workerCount}</strong>
          </div>
          <div className="wx-wl-count-cell">
            <span>Active Assignment</span>
            <strong>{detail.activeAssignmentCount}</strong>
          </div>
        </div>
      </section>

      <section className="wx-wl-dsec">
        <h3>ขั้นตอนถัดไป (Next Actions)</h3>
        <div className="wx-wl-next-actions">
          {nextActions.length === 0 && <span className="wx-wl-next-action-info">ไม่มีขั้นตอนแนะนำเพิ่มเติม</span>}
          {nextActions.map((code) => (
            <span key={code} className="wx-wl-next-action-info">
              {nextActionLabel(code)}
            </span>
          ))}
        </div>
      </section>

      <AgentDelegatedActionsPanel
        agentId={detail.agentId}
        agentDisplayName={detail.displayName}
        organizationSide={detail.organizationSide}
        canIssueInviteLink={canIssueInviteLink}
      />

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--primary wx-wl-drawer-actions-full" onClick={onEdit}>
          แก้ไข Agent
        </button>
      </div>
    </div>
  );
}

/**
 * P161: the picture picker shared by the create and edit forms.
 *
 * The two forms attach the file at different moments, and that difference is
 * real rather than cosmetic: the upload endpoint is keyed by agent id, so on
 * CREATE there is nothing to attach to until the Agent exists. The create form
 * therefore holds the chosen file in its own state and uploads it immediately
 * after the create succeeds; the edit form uploads on save. Either way this
 * component only ever reports the chosen File up to its parent - it performs no
 * request of its own, so there is exactly one place per form that writes.
 *
 * The local preview is built from the chosen File, not from the server, so an
 * operator sees what they picked before anything is sent anywhere.
 */
function AgentPhotoField({ agentId, hasStoredPhoto, displayName, file, onFileChange, disabled }) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!file || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="wx-wl-field wx-wl-photo-field">
      <span>
        {fieldLabel("photo")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
      </span>
      <div className="wx-wl-photo-field-row">
        {previewUrl ? (
          <img className="wx-wl-agent-photo wx-wl-agent-photo--md" src={previewUrl} alt="ตัวอย่างรูปที่เลือก" />
        ) : (
          <AgentPhoto agentId={agentId} hasPhoto={hasStoredPhoto} displayName={displayName} size="md" />
        )}
        <div className="wx-wl-photo-field-controls">
          <input
            ref={inputRef}
            type="file"
            aria-label="รูป Agent"
            // The same three types the server's own allow-list accepts. This
            // only filters the file dialog - the real check is the server's
            // magic-number test, which this can never replace.
            accept="image/jpeg,image/png,image/webp"
            disabled={disabled}
            onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
          />
          {file && (
            <button
              type="button"
              className="wx-button wx-button--ghost"
              onClick={() => {
                onFileChange(null);
                if (inputRef.current) {
                  inputRef.current.value = "";
                }
              }}
            >
              ยกเลิกรูปที่เลือก
            </button>
          )}
          <span className="wx-wl-optional">รองรับ JPG, PNG หรือ WEBP ขนาดไม่เกิน 8 MB</span>
        </div>
      </div>
    </div>
  );
}

const EMPTY_AGENT_FORM = {
  superAgentOrganizationId: "",
  plcOrganizationId: "",
  displayName: "",
  legalName: "",
  ownerName: "",
  primaryPhoneNumber: "",
  lineId: "",
  note: "",
  // Prompt 136: default ON for a new Agent, per the owner's own written
  // requirement ("createLoginAccount checkbox/toggle default ON for new Agent").
  createLoginAccount: true,
  loginIdentifier: "",
  temporaryPassword: "",
};

export function emptyAgentForm(prefill = {}) {
  return { ...EMPTY_AGENT_FORM, ...prefill };
}

/** Shared password input + show/hide toggle (LoginPage.jsx's own isPasswordVisible pattern, plain text toggle button here rather than an SVG icon - simple for the owner-testing use case this field exists for). */
function PasswordField({ label, ariaLabel, value, onChange, autoComplete }) {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <label className="wx-wl-field">
      <span>{label}</span>
      <div className="wx-wl-password-row">
        <input type={isVisible ? "text" : "password"} aria-label={ariaLabel} autoComplete={autoComplete} value={value} onChange={(event) => onChange(event.target.value)} />
        <button type="button" className="wx-button wx-button--ghost" onClick={() => setIsVisible((visible) => !visible)}>
          {isVisible ? "ซ่อน" : "แสดง"}
        </button>
      </div>
    </label>
  );
}

export function AgentCreateView({ titleId, form, onChange, superAgentOptions, plcOptions, plcOptionsStatus, validationError, submitState, onSubmit, photoFile, onPhotoFileChange }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">CREATE AGENT</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        สร้าง Agent
      </h2>
      <p className="wx-wl-drawer-sub">เพิ่ม Agent ใหม่เข้าสายงาน</p>

      <div className="wx-wl-notice">สร้าง/แก้ไขได้เฉพาะ SuperAdmin เท่านั้น ระบบจะปฏิเสธคำขอจากผู้ใช้อื่นโดยอัตโนมัติ</div>

      <label className="wx-wl-field">
        <span>
          superAgentOrganizationId (Super Agent ต้นสังกัด) <b>*</b>
        </span>
        <select
          aria-label="superAgentOrganizationId"
          value={form.superAgentOrganizationId}
          onChange={(event) => onChange({ superAgentOrganizationId: event.target.value, plcOrganizationId: "" })}
        >
          <option value="">เลือก Super Agent...</option>
          {superAgentOptions.map((option) => (
            <option key={option.superAgentOrganizationId} value={option.superAgentOrganizationId}>
              {option.displayName} ({option.superAgentCode})
            </option>
          ))}
        </select>
      </label>
      <label className="wx-wl-field">
        <span>
          plcOrganizationId (PLC ต้นสังกัด) <b>*</b>
        </span>
        <select
          aria-label="plcOrganizationId"
          value={form.plcOrganizationId}
          disabled={!form.superAgentOrganizationId}
          onChange={(event) => onChange({ plcOrganizationId: event.target.value })}
        >
          <option value="">{form.superAgentOrganizationId ? "เลือก PLC..." : "เลือก Super Agent ก่อน"}</option>
          {plcOptions.map((option) => (
            <option key={option.plcOrganizationId} value={option.plcOrganizationId}>
              {option.displayName} ({option.plcCode})
            </option>
          ))}
        </select>
        {form.superAgentOrganizationId && plcOptionsStatus === "ready" && plcOptions.length === 0 && (
          <span className="wx-wl-optional">Super Agent นี้ยังไม่มี PLC - กรุณาสร้าง PLC ก่อน</span>
        )}
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("displayName")} <b>*</b>
        </span>
        <input type="text" aria-label="displayName" value={form.displayName} onChange={(event) => onChange({ displayName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("legalName")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
        </span>
        <input type="text" aria-label="legalName" value={form.legalName} onChange={(event) => onChange({ legalName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("ownerName")}</span>
        <input type="text" aria-label="ผู้ดูแล" value={form.ownerName} onChange={(event) => onChange({ ownerName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("primaryPhoneNumber")}</span>
        <input type="tel" aria-label="primaryPhoneNumber" value={form.primaryPhoneNumber} onChange={(event) => onChange({ primaryPhoneNumber: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("lineId")}</span>
        <input type="text" aria-label="lineId" value={form.lineId} onChange={(event) => onChange({ lineId: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("note")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
        </span>
        <textarea aria-label="note" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
      </label>

      {/* The Agent does not exist yet, so there is nothing to upload to and no
          stored picture to show - the file is held here and attached by the
          page as soon as the create succeeds. */}
      {onPhotoFileChange && (
        <AgentPhotoField
          agentId={null}
          hasStoredPhoto={false}
          displayName={form.displayName}
          file={photoFile}
          onFileChange={onPhotoFileChange}
          disabled={submitting}
        />
      )}

      <section className="wx-wl-dsec">
        <h3>บัญชีเข้าสู่ระบบ Backoffice (Login Account)</h3>
        <label className="wx-wl-checkbox-field">
          <input
            type="checkbox"
            aria-label="createLoginAccount"
            checked={form.createLoginAccount}
            onChange={(event) => onChange({ createLoginAccount: event.target.checked })}
          />
          <span>สร้างบัญชีเข้าสู่ระบบ Backoffice ให้ Agent นี้ทันที</span>
        </label>
        {form.createLoginAccount && (
          <>
            <label className="wx-wl-field">
              <span>
                ชื่อผู้ใช้สำหรับเข้าระบบ <b>*</b> <span className="wx-wl-optional">(เช่น agent001)</span>
              </span>
              <input type="text" aria-label="ชื่อผู้ใช้สำหรับเข้าระบบ" value={form.loginIdentifier} onChange={(event) => onChange({ loginIdentifier: event.target.value })} />
            </label>
            <PasswordField
              label={
                <>
                  รหัสผ่านชั่วคราว <b>*</b> <span className="wx-wl-optional">(อย่างน้อย 6 ตัวอักษร เช่น 123456)</span>
                </>
              }
              ariaLabel="รหัสผ่านชั่วคราว"
              autoComplete="new-password"
              value={form.temporaryPassword}
              onChange={(value) => onChange({ temporaryPassword: value })}
            />
          </>
        )}
      </section>

      {validationError && (
        <p className="wx-wl-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-wl-form-message" role="alert">
          {describeWriteError(submitState.error, "Agent นี้ถูกสร้างไปแล้ว กรุณาลองใหม่อีกครั้ง")}
        </p>
      )}

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--primary wx-wl-drawer-actions-full" disabled={submitting} onClick={onSubmit}>
          {submitting ? "กำลังบันทึก..." : "บันทึก Agent"}
        </button>
      </div>
    </div>
  );
}

/** Prompt 136: shown once, right after CreateAgent (with createLoginAccount) or a login-account create/reset succeeds - the credentials are never re-fetched from the server, only echoed from the caller's own just-submitted form state, then discarded when this view closes (DetailDrawer unmounts it). */
export function AgentLoginCreatedView({ titleId, agentDisplayName, loginIdentifier, temporaryPassword, wasReset, onDone }) {
  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">{wasReset ? "PASSWORD RESET" : "LOGIN ACCOUNT CREATED"}</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        {wasReset ? "รีเซ็ตรหัสผ่านสำเร็จ" : "สร้างบัญชีเข้าสู่ระบบสำเร็จ"}
      </h2>
      <p className="wx-wl-drawer-sub">{agentDisplayName}</p>

      <div className="wx-wl-credential-box">
        <div className="wx-wl-credential-row">
          <span>ชื่อผู้ใช้สำหรับเข้าระบบ</span>
          <strong>{loginIdentifier}</strong>
        </div>
        <div className="wx-wl-credential-row">
          <span>รหัสผ่านชั่วคราว</span>
          <strong>{temporaryPassword}</strong>
        </div>
        <p className="wx-wl-credential-warning">แสดงรหัสผ่านนี้เพียงครั้งเดียว กรุณาบันทึกไว้ก่อนปิดหน้าต่างนี้ ระบบจะไม่แสดงซ้ำอีก</p>
      </div>

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--primary wx-wl-drawer-actions-full" onClick={onDone}>
          ปิด
        </button>
      </div>
    </div>
  );
}

/** Prompt 136: creates a login account for an Agent that has none, or resets the password of an existing one - same form/route either way. */
export function AgentLoginAccountView({ titleId, agentDisplayName, hasLoginAccount, form, onChange, validationError, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">{hasLoginAccount ? "RESET PASSWORD" : "CREATE LOGIN ACCOUNT"}</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        {hasLoginAccount ? "รีเซ็ตรหัสผ่าน Agent" : "สร้างบัญชีเข้าสู่ระบบ Agent"}
      </h2>
      <p className="wx-wl-drawer-sub">{agentDisplayName}</p>

      <div className="wx-wl-notice">ดำเนินการได้เฉพาะ SuperAdmin เท่านั้น ระบบจะปฏิเสธคำขอจากผู้ใช้อื่นโดยอัตโนมัติ</div>

      <label className="wx-wl-field">
        <span>
          ชื่อผู้ใช้สำหรับเข้าระบบ <b>*</b> <span className="wx-wl-optional">(เช่น agent001)</span>
        </span>
        <input type="text" aria-label="ชื่อผู้ใช้สำหรับเข้าระบบ" value={form.loginIdentifier} onChange={(event) => onChange({ loginIdentifier: event.target.value })} />
      </label>
      <PasswordField
        label={
          <>
            รหัสผ่านชั่วคราว <b>*</b> <span className="wx-wl-optional">(อย่างน้อย 6 ตัวอักษร)</span>
          </>
        }
        ariaLabel="รหัสผ่านชั่วคราว"
        autoComplete="new-password"
        value={form.temporaryPassword}
        onChange={(value) => onChange({ temporaryPassword: value })}
      />

      {validationError && (
        <p className="wx-wl-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-wl-form-message" role="alert">
          {describeWriteError(submitState.error, "loginIdentifier นี้ถูกใช้งานแล้ว กรุณาใช้ค่าอื่น")}
        </p>
      )}

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : hasLoginAccount ? "รีเซ็ตรหัสผ่าน" : "สร้างบัญชี"}
        </button>
      </div>
    </div>
  );
}

export function AgentEditView({ titleId, code, form, onChange, validationError, submitState, onSubmit, onBack, agentId, hasStoredPhoto, photoFile, onPhotoFileChange }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-wl-drawer">
      <span className="wx-wl-drawer-kicker">EDIT AGENT</span>
      <h2 id={titleId} className="wx-wl-drawer-title">
        แก้ไข Agent
      </h2>
      <p className="wx-wl-drawer-sub">{code ?? "-"}</p>

      <label className="wx-wl-field">
        <span>
          {fieldLabel("displayName")} <b>*</b>
        </span>
        <input type="text" aria-label="displayName" value={form.displayName} onChange={(event) => onChange({ displayName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("legalName")}</span>
        <input type="text" aria-label="legalName" value={form.legalName} onChange={(event) => onChange({ legalName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("ownerName")}</span>
        <input type="text" aria-label="ผู้ดูแล" value={form.ownerName} onChange={(event) => onChange({ ownerName: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("primaryPhoneNumber")}</span>
        <input type="tel" aria-label="primaryPhoneNumber" value={form.primaryPhoneNumber} onChange={(event) => onChange({ primaryPhoneNumber: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("lineId")}</span>
        <input type="text" aria-label="lineId" value={form.lineId} onChange={(event) => onChange({ lineId: event.target.value })} />
      </label>
      <label className="wx-wl-field">
        <span>{fieldLabel("status")}</span>
        <select aria-label="status" value={form.status} onChange={(event) => onChange({ status: event.target.value })}>
          {WORKER_LINE_STATUS_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <label className="wx-wl-field">
        <span>
          {fieldLabel("note")} <span className="wx-wl-optional">(ไม่บังคับ)</span>
        </span>
        <textarea aria-label="note" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
      </label>

      {/* Choosing a new file here replaces the stored picture when Save runs.
          Leaving it alone keeps whatever is already attached - the photo write
          is a separate call the page only makes when a file was actually
          picked, so saving an unrelated edit can never blank the picture. */}
      {onPhotoFileChange && (
        <AgentPhotoField
          agentId={agentId}
          hasStoredPhoto={hasStoredPhoto}
          displayName={form.displayName}
          file={photoFile}
          onFileChange={onPhotoFileChange}
          disabled={submitting}
        />
      )}

      {validationError && (
        <p className="wx-wl-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-wl-form-message" role="alert">
          {describeWriteError(submitState.error, "Agent นี้ถูกแก้ไขไปแล้วก่อนหน้านี้ กรุณารีเฟรชแล้วลองใหม่")}
        </p>
      )}

      <div className="wx-wl-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
        </button>
      </div>
    </div>
  );
}
